import React from 'react';
import Head from 'next/head';
import NavBar from '../components/navbar';
import Layout from '../components/layout';
import Container from '../components/container';
import config from '../lib/config';
import PostBody from '../components/post-body';

import { GetStaticProps } from 'next';
import { fetchAPI, getPrimaryMenuItems } from '../lib/api';
import { ISponsor } from '../models/sponsor';
import { IMenuItem } from '../models/menu-item';
import { IFeaturedImage } from '../models/featured-image';
import { Disclosure } from '@headlessui/react';
import { H2, H3, H6, P } from '../components/text';
import { ChevronUpIcon } from '@heroicons/react/20/solid';
import { IYoastMeta } from '../models/post';

interface IProps {
	sponsors: ISponsor[];
	primaryMenuItems: IMenuItem[];
	page: {
		title: string;
		content: string | TrustedHTML;
		featuredImage: IFeaturedImage;
		faqs_group: IFaqsGroup;
		seo: IYoastMeta;
	};
}

export interface IFaqsGroup {
	faqs: IFaq[];
}

export interface IFaq {
	answer: string;
	question: string;
}

export default function FaqPage({ page, primaryMenuItems, sponsors }: IProps) {
	const pageTitle = `${page.title} | ${config.siteName}`;
	const meta = page.seo?.description || page.title;

	return (
		<Layout preview sponsors={sponsors} metaTitle={pageTitle} metaDesc={meta}>
			<Head>
				<title>{pageTitle}</title>
			</Head>
			<NavBar items={primaryMenuItems} />

			<Container className="py-12 xl:py-16 max-w-4xl">
				<section className="pb-12">
					<H2 className="mb-4">{page.title}</H2>
					<article>{page.content && <PostBody content={page.content} />}</article>
				</section>

				<section className="pb-12 xl:pb-32">
					<H3 className="mb-6">Veelgestelde vragen</H3>
					{page.faqs_group.faqs.map((item, i) => (
						<Disclosure key={i}>
							{({ open }) => (
								<div className="mb-[1px]">
									<Disclosure.Button className="flex w-full justify-between  px-4 py-4 text-left text-black-500 bg-white hover:cursor-pointer">
										<H6>{item.question}</H6>
										<ChevronUpIcon
											className={`${
												open ? 'rotate-180 transform transition-all' : ' transition-all'
											} h-6 w-6 text-black-500`}
										/>
									</Disclosure.Button>
									<Disclosure.Panel className="px-4 pb-2 bg-white ">
										<P className="mb-4">{item.answer}</P>
									</Disclosure.Panel>
								</div>
							)}
						</Disclosure>
					))}
				</section>
			</Container>
		</Layout>
	);
}

export const getStaticProps: GetStaticProps = async ({ preview = false }) => {
	const primaryMenuItems = await getPrimaryMenuItems();
	const data = await fetchAPI<{ page: any; sponsors: any }>(
		`
		query {
			page(id: 112605, idType: DATABASE_ID) {
				title
				content
				faqs_group {
					faqs {
						answer
						question
					}
				}
			}
			sponsors(first: 20) {
				nodes {
					title
					sponsor_fields {
						subtitle
						websiteUrl
						mainSponsor
						invertedImage {
							sourceUrl
						}
					}
				}
			}
		}
	`,
	);

	const { sponsors, page } = data;

	return {
		props: {
			page,
			sponsors: sponsors.nodes,
			preview,
			primaryMenuItems,
		},
		revalidate: 3600,
	};
};
